import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import EquipmentMasterCatalogApi from "../../../../../infrastructure/api/equipmentMasterCatalog";

const EMPTY_FORM = {
  categoryId: "",
  brandText: "",
  modelText: "",
  description: "",
  datasheetUrl: "",
};

/**
 * Shared dialog for suggesting a master-catalog equipment item that is missing.
 * submitRequest must return { ok, message }.
 */
const NewEquipmentItemRequestDialog = ({
  open,
  onClose,
  onSubmitted,
  submitRequest,
  title = "درخواست افزودن تجهیز به کاتالوگ مرجع",
}) => {
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM);
    EquipmentMasterCatalogApi.getPickerData().then((r) =>
      setCategories(r?.data?.categories || r?.data?.Categories || [])
    );
  }, [open]);

  const setField = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async () => {
    if (!form.categoryId) {
      toast.warn("دسته الزامی است.");
      return;
    }
    if (!form.brandText?.trim() && !form.modelText?.trim()) {
      toast.warn("برند یا مدل الزامی است.");
      return;
    }
    if (typeof submitRequest !== "function") {
      toast.error("ثبت درخواست پیکربندی نشده است.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await submitRequest({
        categoryId: Number(form.categoryId),
        brandText: form.brandText?.trim() || "",
        modelText: form.modelText?.trim() || "",
        description: form.description?.trim() || "",
        datasheetUrl: form.datasheetUrl?.trim() || "",
      });
      if (res?.ok) {
        toast.success("درخواست تجهیز جدید ثبت شد");
        onSubmitted?.(res.data);
        onClose?.();
      } else {
        toast.error(res?.message || "خطا در ثبت درخواست");
      }
    } catch (e) {
      toast.error(e?.message || "خطا در ثبت درخواست");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            select
            size="small"
            label="دسته *"
            SelectProps={{ native: true }}
            value={form.categoryId}
            onChange={(e) => setField("categoryId", e.target.value)}
          >
            <option value="">—</option>
            {categories.map((c) => (
              <option key={c.id ?? c.Id} value={c.id ?? c.Id}>
                {c.titleFa ?? c.TitleFa} ({c.codePrefix ?? c.CodePrefix})
              </option>
            ))}
          </TextField>
          <TextField
            size="small"
            label="برند"
            value={form.brandText}
            onChange={(e) => setField("brandText", e.target.value)}
          />
          <TextField
            size="small"
            label="مدل"
            value={form.modelText}
            onChange={(e) => setField("modelText", e.target.value)}
          />
          <TextField
            size="small"
            multiline
            minRows={2}
            label="توضیحات"
            value={form.description}
            onChange={(e) => setField("description", e.target.value)}
          />
          <TextField
            size="small"
            label="لینک دیتاشیت"
            value={form.datasheetUrl}
            onChange={(e) => setField("datasheetUrl", e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          انصراف
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          ثبت درخواست
        </Button>
      </DialogActions>
    </Dialog>
  );
};

NewEquipmentItemRequestDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmitted: PropTypes.func,
  submitRequest: PropTypes.func.isRequired,
  title: PropTypes.string,
};

export default NewEquipmentItemRequestDialog;
